import { PayableRow } from '@bariendo/lib/payables';
import { TextInput } from '@mantine/core';
import { ReactElement, useEffect, useState } from 'react';
import { alert } from '@/services/alert';
import { ORBIT_NEEDS_ACTION } from '@/theme/colors';
import { formatLedgerMoney } from '../ledger/ledgerRows';
import { actualAmount, actualDiffers } from './payableRows';
import { PayableActions } from './usePayableActions';

const asText = (value: number | null) => (value === null ? '' : value.toFixed(2));

/** Actual, editable until paid; amber with the schedule on hover once it departs from it (BAR-3046). */
export function ActualCell({ row, actions }: { row: PayableRow; actions: PayableActions }): ReactElement {
  const shown = asText(actualAmount(row));
  const [value, setValue] = useState(shown);
  useEffect(() => setValue(shown), [shown]);
  const differs = actualDiffers(row);

  const commit = async (): Promise<void> => {
    const parsed = Number.parseFloat(value.replace(/−/g, '-').replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(parsed) || asText(parsed) === shown) {
      setValue(shown);
      return;
    }
    try {
      await actions.setActual(row, Math.round(parsed * 100) / 100);
    } catch (error) {
      setValue(shown);
      alert.error(`Couldn't set the Actual: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  };

  return (
    <TextInput
      size="xs"
      aria-label="Actual"
      inputMode="decimal"
      value={value}
      disabled={row.status === 'PAID'}
      title={differs && row.scheduledAmount !== null ? `Schedule ${formatLedgerMoney(row.scheduledAmount)}` : undefined}
      onChange={(event) => setValue(event.currentTarget.value)}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
      }}
      styles={{
        input: {
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          ...(differs ? { color: ORBIT_NEEDS_ACTION, fontWeight: 600 } : {}),
        },
      }}
    />
  );
}
